import React, { useState, useEffect } from 'react';
import Header from '../../components/navigation/Header';
import GlobalFilterBar from '../../components/navigation/GlobalFilterBar';
import ComplianceAlertBanner from '../../components/navigation/ComplianceAlertBanner';
import DataRefreshIndicator from '../../components/navigation/DataRefreshIndicator';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import payrollService from '../../services/payrollService';
import payrollAutomationService from '../../services/payrollAutomationService';
import approvalWorkflowService from '../../services/approvalWorkflowService';
import ScheduleConfigModal from './components/ScheduleConfigModal';
import BatchApprovalCard from './components/BatchApprovalCard';
import PayslipList from './components/PayslipList';
import BulkProcessingLog from './components/BulkProcessingLog';
import jsPDF from 'jspdf';
import LegalDisclaimerBanner from '../../components/navigation/LegalDisclaimerBanner';
import TeaserEditModal from '../../components/TeaserEditModal';
import { useTeaserEdit } from '../../hooks/useTeaserEdit';
import { billingService } from '../../services/billingService';
import { supabase } from '../../lib/supabase';

const PayrollManagement = () => {
  const [payrollRuns, setPayrollRuns] = useState([]);
  const [swtFilings, setSwtFilings] = useState([]);
  const [nasfundContributions, setNasfundContributions] = useState([]);
  const [minimumWage, setMinimumWage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Automation states
  const [activeTab, setActiveTab] = useState('overview');
  const [schedules, setSchedules] = useState([]);
  const [batchApprovals, setBatchApprovals] = useState([]);
  const [payslips, setPayslips] = useState([]);
  const [processingLogs, setProcessingLogs] = useState([]);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [selectedSchedule, setSelectedSchedule] = useState(null);
  const [bulkProcessing, setBulkProcessing] = useState(false);
  const [payslipGenerating, setPayslipGenerating] = useState(false);
  const [approvalInstances, setApprovalInstances] = useState([]);
  const [subscriptionStatus, setSubscriptionStatus] = useState('active');
  
  const { 
    editingField: teaserEditField, 
    showRedirectModal, 
    startEdit, 
    handleInput: handleTeaserInput, 
    closeModal,
    isEditable 
  } = useTeaserEdit(subscriptionStatus === 'active');
  
  const handleFilterChange = (filters) => {
    // Filter logic can be implemented here if needed
    console.log('Filters changed:', filters);
  };

  useEffect(() => {
    loadData();
    checkSubscription();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [runs, swt, nasfund, minWage, scheds, approvals, slips, logs, workflowApprovals] = await Promise.all([
        payrollService?.getAllRuns(),
        payrollService?.getSWTFilings(),
        payrollService?.getNasfundContributions(),
        payrollService?.getCurrentMinimumWage(),
        payrollAutomationService?.getAllSchedules(),
        payrollAutomationService?.getAllBatchApprovals(),
        payrollAutomationService?.getAllPayslips(),
        payrollAutomationService?.getBulkProcessingLogs(),
        approvalWorkflowService?.getAllApprovalInstances()
      ]);
      setPayrollRuns(runs);
      setSwtFilings(swt);
      setNasfundContributions(nasfund);
      setMinimumWage(minWage);
      setSchedules(scheds);
      setBatchApprovals(approvals);
      setPayslips(slips);
      setProcessingLogs(logs);
      setApprovalInstances(workflowApprovals?.filter(a => a?.entityType === 'payroll_batch'));
    } catch (err) {
      setError(err?.message || 'Failed to load payroll data');
    } finally {
      setLoading(false);
    }
  };

  const checkSubscription = async () => {
    try {
      const { data: user } = await supabase?.auth?.getUser();
      if (user?.user?.id) {
        const { data: companySettings } = await supabase?.from('company_settings')?.select('id')?.eq('user_id', user?.user?.id)?.single();
        
        if (companySettings?.id) {
          const subscriptionDetails = await billingService?.getSubscriptionDetails(companySettings?.id);
          const status = subscriptionDetails?.subscriptionStatus || 'suspended';
          setSubscriptionStatus(status);
        }
      }
    } catch (error) {
      console.error('Error checking subscription:', error);
    }
  };

  const handleSaveSchedule = async (scheduleData) => {
    try {
      if (selectedSchedule) {
        await payrollAutomationService?.updateSchedule(selectedSchedule?.id, scheduleData);
      } else {
        await payrollAutomationService?.createSchedule({
          ...scheduleData,
          createdBy: 'admin@example.com'
        });
      }
      setShowScheduleModal(false);
      setSelectedSchedule(null);
      loadData();
    } catch (err) {
      setError(err?.message || 'Failed to save schedule');
    }
  };

  const handleRunBulkProcessing = async (scheduleId) => {
    try {
      setBulkProcessing(true);
      const schedule = schedules?.find(s => s?.id === scheduleId);
      if (!schedule) throw new Error('Schedule not found');
      
      await payrollAutomationService?.processBulkSalary(
        scheduleId,
        schedule?.departments,
        'admin@example.com'
      );
      
      loadData();
    } catch (err) {
      setError(err?.message || 'Failed to process bulk salary');
    } finally {
      setBulkProcessing(false);
    }
  };

  const handleApproveBatch = async (approvalId) => {
    try {
      const approval = batchApprovals?.find(a => a?.id === approvalId);
      
      // Check if approval workflow exists
      const existingWorkflow = approvalInstances?.find(a => a?.entityId === approvalId);
      
      if (existingWorkflow) {
        // Use approval workflow
        const pendingSteps = await approvalWorkflowService?.getPendingApprovals();
        const currentStep = pendingSteps?.find(s => s?.entityId === approvalId);
        
        if (currentStep) {
          await approvalWorkflowService?.approveStep(
            currentStep?.id,
            'admin@example.com',
            'Approved from payroll management'
          );
        }
      } else {
        // Initiate approval workflow for high-value batches
        if (approval?.totalAmount >= 50000) {
          const metadata = {
            amount: approval?.totalAmount,
            department: approval?.departmentsIncluded?.[0] || 'General',
            employee_count: approval?.totalEmployees
          };
          
          await approvalWorkflowService?.initiateApprovalWorkflow(
            'payroll_batch',
            approvalId,
            metadata,
            'admin@example.com'
          );
        } else {
          // Direct approval for low-value batches
          await payrollAutomationService?.approveBatch(approvalId, 'admin@example.com');
        }
      }
      
      loadData();
    } catch (err) {
      setError(err?.message || 'Failed to approve batch');
    }
  };

  const handleRejectBatch = async (approvalId) => {
    try {
      // Check if approval workflow exists
      const existingWorkflow = approvalInstances?.find(a => a?.entityId === approvalId);
      
      if (existingWorkflow) {
        // Use approval workflow
        const pendingSteps = await approvalWorkflowService?.getPendingApprovals();
        const currentStep = pendingSteps?.find(s => s?.entityId === approvalId);
        
        if (currentStep) {
          const reason = prompt('Enter rejection reason:');
          if (reason) {
            await approvalWorkflowService?.rejectStep(
              currentStep?.id,
              'admin@example.com',
              reason
            );
          }
        }
      } else {
        // Direct rejection
        const reason = prompt('Enter rejection reason:');
        if (reason) {
          await payrollAutomationService?.rejectBatch(approvalId, reason);
        }
      }
      
      loadData();
    } catch (err) {
      setError(err?.message || 'Failed to reject batch');
    }
  };

  const handleGeneratePayslips = async (payrollRunId) => {
    try {
      setPayslipGenerating(true);
      const result = await payrollAutomationService?.generateBulkPayslips(payrollRunId);
      alert(`Payslips generated: ${result?.successful} successful, ${result?.failed} failed`);
      loadData();
    } catch (err) {
      setError(err?.message || 'Failed to generate payslips');
    } finally {
      setPayslipGenerating(false);
    }
  };

  const handleDownloadPayslipPDF = async (payslip) => {
    try {
      const doc = new jsPDF();
      
      // Header
      doc?.setFontSize(20);
      doc?.text('PAYSLIP', 105, 20, { align: 'center' });
      
      // Employee info
      doc?.setFontSize(12);
      doc?.text(`Employee: ${payslip?.employee?.firstName} ${payslip?.employee?.lastName}`, 20, 40);
      doc?.text(`Department: ${payslip?.employee?.department}`, 20, 50);
      doc?.text(`Payslip Number: ${payslip?.payslipNumber}`, 20, 60);
      
      // Pay period
      doc?.text(`Pay Period: ${new Date(payslip?.payPeriodStart)?.toLocaleDateString()} - ${new Date(payslip?.payPeriodEnd)?.toLocaleDateString()}`, 20, 70);
      doc?.text(`Payment Date: ${new Date(payslip?.paymentDate)?.toLocaleDateString()}`, 20, 80);
      
      // Footer
      doc?.setFontSize(10);
      doc?.text('This is a computer-generated document. No signature required.', 105, 280, { align: 'center' });
      
      // Download
      doc?.save(`payslip-${payslip?.payslipNumber}.pdf`);
    } catch (err) {
      setError(err?.message || 'Failed to generate PDF');
    }
  };

  const pendingFilings = [
    ...(swtFilings?.filter(f => f?.filingStatus === 'pending') || []),
    ...(nasfundContributions?.filter(c => c?.filingStatus === 'pending') || [])
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <GlobalFilterBar onFilterChange={handleFilterChange} />
        <ComplianceAlertBanner />
        <DataRefreshIndicator />
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <Icon name="Loader2" className="animate-spin h-8 w-8 mx-auto mb-4 text-primary" />
            <p className="text-muted-foreground">Loading payroll data...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <ComplianceAlertBanner />
      <LegalDisclaimerBanner />
      <GlobalFilterBar onFilterChange={handleFilterChange} />
      <DataRefreshIndicator />
      <main className="container mx-auto px-6 py-8 mt-16">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-foreground mb-2">Payroll & Wages Management</h1>
          <p className="text-muted-foreground">Calculate wages, overtime, SWT, and Nasfund contributions</p>
        </div>

        {error && (
          <div className="bg-error/10 border border-error text-error px-4 py-3 rounded-md mb-6">
            <p className="font-medium">Error loading data</p>
            <p className="text-sm">{error}</p>
          </div>
        )}

        {/* Tab Navigation */}
        <div className="flex items-center gap-2 mb-6 border-b border-border">
          <button
            onClick={() => setActiveTab('overview')}
            className={`px-4 py-2 font-medium transition-colors ${
              activeTab === 'overview' ?'text-primary border-b-2 border-primary' :'text-muted-foreground hover:text-foreground'
            }`}
          >
            <Icon name="LayoutDashboard" size={18} className="inline mr-2" />
            Overview
          </button>
          <button
            onClick={() => setActiveTab('schedules')}
            className={`px-4 py-2 font-medium transition-colors ${
              activeTab === 'schedules' ?'text-primary border-b-2 border-primary' :'text-muted-foreground hover:text-foreground'
            }`}
          >
            <Icon name="Calendar" size={18} className="inline mr-2" />
            Schedules
          </button>
          <button
            onClick={() => setActiveTab('approvals')}
            className={`px-4 py-2 font-medium transition-colors ${
              activeTab === 'approvals' ?'text-primary border-b-2 border-primary' :'text-muted-foreground hover:text-foreground'
            }`}
          >
            <Icon name="CheckSquare" size={18} className="inline mr-2" />
            Batch Approvals
            {batchApprovals?.filter(a => a?.approvalStatus === 'pending')?.length > 0 && (
              <span className="ml-2 px-2 py-0.5 bg-warning text-white text-xs rounded-full">
                {batchApprovals?.filter(a => a?.approvalStatus === 'pending')?.length}
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveTab('payslips')}
            className={`px-4 py-2 font-medium transition-colors ${
              activeTab === 'payslips' ?'text-primary border-b-2 border-primary' :'text-muted-foreground hover:text-foreground'
            }`}
          >
            <Icon name="FileText" size={18} className="inline mr-2" />
            Payslips
          </button>
          <button
            onClick={() => setActiveTab('logs')}
            className={`px-4 py-2 font-medium transition-colors ${
              activeTab === 'logs' ?'text-primary border-b-2 border-primary' :'text-muted-foreground hover:text-foreground'
            }`}
          >
            <Icon name="Activity" size={18} className="inline mr-2" />
            Processing Logs
          </button>
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
              <div className="bg-card rounded-lg shadow-sm p-6 border border-border">
                <div className="flex items-center justify-between mb-2">
                  <Icon name="DollarSign" size={24} className="text-success" />
                  <span className="text-2xl font-bold text-foreground">K{minimumWage?.hourlyRate?.toFixed(2)}</span>
                </div>
                <p className="text-sm text-muted-foreground">Minimum Wage (per hour)</p>
                <p className="text-xs text-muted-foreground mt-1">Effective: {new Date(minimumWage?.effectiveDate)?.toLocaleDateString()}</p>
              </div>

              <div className="bg-card rounded-lg shadow-sm p-6 border border-border">
                <div className="flex items-center justify-between mb-2">
                  <Icon name="FileText" size={24} className="text-primary" />
                  <span className="text-2xl font-bold text-foreground">{payrollRuns?.length || 0}</span>
                </div>
                <p className="text-sm text-muted-foreground">Payroll Runs</p>
              </div>

              <div className="bg-card rounded-lg shadow-sm p-6 border border-border">
                <div className="flex items-center justify-between mb-2">
                  <Icon name="AlertTriangle" size={24} className="text-warning" />
                  <span className="text-2xl font-bold text-foreground">{pendingFilings?.length || 0}</span>
                </div>
                <p className="text-sm text-muted-foreground">Pending Filings</p>
              </div>

              <div className="bg-card rounded-lg shadow-sm p-6 border border-border">
                <div className="flex items-center justify-between mb-2">
                  <Icon name="TrendingUp" size={24} className="text-accent" />
                  <span className="text-2xl font-bold text-foreground">100%</span>
                </div>
                <p className="text-sm text-muted-foreground">Compliance Rate</p>
              </div>
            </div>

            {pendingFilings?.length > 0 && (
              <div className="bg-warning/10 border border-warning rounded-lg p-6 mb-8">
                <div className="flex items-start gap-3 mb-4">
                  <Icon name="AlertTriangle" size={24} className="text-warning flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="text-lg font-semibold text-foreground mb-1">Pending Filings</h3>
                    <p className="text-sm text-muted-foreground">The following filings require submission</p>
                  </div>
                </div>
                <div className="space-y-3">
                  {swtFilings?.filter(f => f?.filingStatus === 'pending')?.map((filing) => {
                    const daysUntilDue = Math.floor((new Date(filing?.dueDate) - new Date()) / (1000 * 60 * 60 * 24));
                    return (
                      <div key={filing?.id} className="bg-card rounded-md p-4 border border-border">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium text-foreground">SWT Filing - {filing?.filingPeriod}</p>
                            <p className="text-sm text-muted-foreground">Total SWT: K{filing?.totalSwt?.toLocaleString()}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-sm font-medium text-warning">
                              {daysUntilDue > 0 ? `${daysUntilDue} days remaining` : 'OVERDUE'}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              Due: {new Date(filing?.dueDate)?.toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  {nasfundContributions?.filter(c => c?.filingStatus === 'pending')?.map((contribution) => {
                    const daysUntilDue = Math.floor((new Date(contribution?.dueDate) - new Date()) / (1000 * 60 * 60 * 24));
                    return (
                      <div key={contribution?.id} className="bg-card rounded-md p-4 border border-border">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium text-foreground">Nasfund Contribution - {contribution?.contributionPeriod}</p>
                            <p className="text-sm text-muted-foreground">
                              Employee: K{contribution?.totalEmployeeContribution?.toLocaleString()} • 
                              Employer: K{contribution?.totalEmployerContribution?.toLocaleString()}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="text-sm font-medium text-warning">
                              {daysUntilDue > 0 ? `${daysUntilDue} days remaining` : 'OVERDUE'}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              Due: {new Date(contribution?.dueDate)?.toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="bg-card rounded-lg shadow-sm border border-border p-6 mb-8">
              <h2 className="text-xl font-semibold text-foreground mb-4">Payroll Calculation Guide</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="border border-border rounded-lg p-4">
                  <h3 className="font-semibold text-foreground mb-3 flex items-center gap-2">
                    <Icon name="Calculator" size={18} className="text-primary" />
                    Base Pay Calculation
                  </h3>
                  <div className="space-y-2 text-sm">
                    <p className="text-muted-foreground">Hourly Workers:</p>
                    <p className="font-mono bg-muted p-2 rounded">Base Pay = Hours Worked × Hourly Rate</p>
                    <p className="text-muted-foreground mt-3">Salaried Workers:</p>
                    <p className="font-mono bg-muted p-2 rounded">Base Pay = Annual Salary ÷ Pay Periods</p>
                  </div>
                </div>

                <div className="border border-border rounded-lg p-4">
                  <h3 className="font-semibold text-foreground mb-3 flex items-center gap-2">
                    <Icon name="Clock" size={18} className="text-primary" />
                    Overtime Calculation
                  </h3>
                  <div className="space-y-2 text-sm">
                    <p className="text-muted-foreground">Standard Multiplier: 1.5x</p>
                    <p className="font-mono bg-muted p-2 rounded">Overtime Pay = OT Hours × Hourly Rate × 1.5</p>
                    <p className="text-xs text-muted-foreground mt-2">Configurable per employment contract</p>
                  </div>
                </div>

                <div className="border border-border rounded-lg p-4">
                  <h3 className="font-semibold text-foreground mb-3 flex items-center gap-2">
                    <Icon name="Receipt" size={18} className="text-primary" />
                    SWT (Salary Wages Tax)
                  </h3>
                  <div className="space-y-2 text-sm">
                    <p className="text-muted-foreground">Calculated per IRC tax schedules</p>
                    <p className="text-xs text-muted-foreground">Monthly lodgment to Internal Revenue Commission</p>
                    <Button variant="outline" size="sm" className="mt-2" iconName="ExternalLink">
                      myIRC Portal
                    </Button>
                  </div>
                </div>

                <div className="border border-border rounded-lg p-4">
                  <h3 className="font-semibold text-foreground mb-3 flex items-center gap-2">
                    <Icon name="PiggyBank" size={18} className="text-primary" />
                    Nasfund Superannuation
                  </h3>
                  <div className="space-y-2 text-sm">
                    <p className="text-muted-foreground">Standard Rates:</p>
                    <p className="font-mono bg-muted p-2 rounded">Employee: 6% • Employer: 8.4%</p>
                    <p className="text-xs text-muted-foreground mt-2">Of superable earnings</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-card rounded-lg shadow-sm border border-border p-6">
              <h2 className="text-xl font-semibold text-foreground mb-4">Recent Payroll Runs</h2>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Pay Period</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Payment Date</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Status</th>
                      <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">Gross Pay</th>
                      <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">SWT</th>
                      <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">Nasfund</th>
                      <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">Net Pay</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {payrollRuns?.length === 0 ? (
                      <tr>
                        <td colSpan="8" className="text-center py-8 text-muted-foreground">
                          No payroll runs found
                        </td>
                      </tr>
                    ) : (
                      payrollRuns?.map((run) => (
                        <tr key={run?.id} className="border-b border-border hover:bg-muted/50 transition-colors">
                          <td className="py-3 px-4 text-sm text-foreground">
                            {new Date(run?.payPeriodStart)?.toLocaleDateString()} - {new Date(run?.payPeriodEnd)?.toLocaleDateString()}
                          </td>
                          <td className="py-3 px-4 text-sm text-foreground">
                            {new Date(run?.paymentDate)?.toLocaleDateString()}
                          </td>
                          <td className="py-3 px-4">
                            {run?.payrollStatus === 'processed' ? (
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-success/10 text-success">
                                <Icon name="CheckCircle" size={12} className="mr-1" />
                                Processed
                              </span>
                            ) : (
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-muted text-muted-foreground">
                                {run?.payrollStatus?.toUpperCase()}
                              </span>
                            )}
                          </td>
                          <td className="py-3 px-4 text-sm text-right font-medium text-foreground">
                            K{run?.totalGross?.toLocaleString()}
                          </td>
                          <td className="py-3 px-4 text-sm text-right text-foreground">
                            K{run?.totalSwt?.toLocaleString()}
                          </td>
                          <td className="py-3 px-4 text-sm text-right text-foreground">
                            K{run?.totalNasfund?.toLocaleString()}
                          </td>
                          <td className="py-3 px-4 text-sm text-right font-medium text-success">
                            K{run?.totalNet?.toLocaleString()}
                          </td>
                          <td className="py-3 px-4">
                            <Button variant="ghost" size="sm" iconName="Eye">
                              View
                            </Button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}

        {/* Schedules Tab */}
        {activeTab === 'schedules' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold text-foreground">Payroll Schedules</h2>
              <Button
                variant="primary"
                iconName="Plus"
                onClick={() => {
                  setSelectedSchedule(null);
                  setShowScheduleModal(true);
                }}
              >
                Create Schedule
              </Button>
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Icon name="Loader2" className="animate-spin h-8 w-8 text-primary" />
              </div>
            ) : schedules?.length === 0 ? (
              <div className="text-center py-12 bg-card rounded-lg border border-border">
                <Icon name="Calendar" size={48} className="mx-auto mb-4 text-muted-foreground" />
                <p className="text-muted-foreground mb-4">No schedules configured yet</p>
                <Button variant="primary" iconName="Plus" onClick={() => setShowScheduleModal(true)}>
                  Create First Schedule
                </Button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {schedules?.map((schedule) => (
                  <div key={schedule?.id} className="bg-card border border-border rounded-lg p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-foreground mb-1">
                          {schedule?.scheduleName}
                        </h3>
                        <p className="text-sm text-muted-foreground">
                          {schedule?.frequency?.toUpperCase()} • {schedule?.departments?.length} departments
                        </p>
                      </div>
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                        schedule?.isActive
                          ? 'bg-success/10 text-success' :'bg-muted text-muted-foreground'
                      }`}>
                        {schedule?.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </div>

                    <div className="space-y-2 mb-4">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground">Next Run:</span>
                        <span className="font-medium text-foreground">
                          {new Date(schedule?.nextRunDate)?.toLocaleDateString()}
                        </span>
                      </div>
                      {schedule?.lastRunDate && (
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-muted-foreground">Last Run:</span>
                          <span className="font-medium text-foreground">
                            {new Date(schedule?.lastRunDate)?.toLocaleDateString()}
                          </span>
                        </div>
                      )}
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground">Auto-approve:</span>
                        <span className="font-medium text-foreground">
                          {schedule?.autoApprove ? 'Yes' : 'No'}
                        </span>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 pt-4 border-t border-border">
                      <Button
                        variant="primary"
                        size="sm"
                        iconName="Play"
                        onClick={() => handleRunBulkProcessing(schedule?.id)}
                        disabled={bulkProcessing || !schedule?.isActive}
                      >
                        {bulkProcessing ? 'Processing...' : 'Run Now'}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        iconName="Edit"
                        onClick={() => {
                          setSelectedSchedule(schedule);
                          setShowScheduleModal(true);
                        }}
                      >
                        Edit
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Batch Approvals Tab */}
        {activeTab === 'approvals' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold text-foreground">Batch Approvals</h2>
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">
                  {batchApprovals?.filter(a => a?.approvalStatus === 'pending')?.length} pending
                </span>
              </div>
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Icon name="Loader2" className="animate-spin h-8 w-8 text-primary" />
              </div>
            ) : batchApprovals?.length === 0 ? (
              <div className="text-center py-12 bg-card rounded-lg border border-border">
                <Icon name="CheckSquare" size={48} className="mx-auto mb-4 text-muted-foreground" />
                <p className="text-muted-foreground">No batch approvals yet</p>
              </div>
            ) : (
              <div className="space-y-4">
                {batchApprovals?.map((approval) => (
                  <BatchApprovalCard
                    key={approval?.id}
                    approval={approval}
                    onApprove={handleApproveBatch}
                    onReject={handleRejectBatch}
                  />
                ))}
              </div>
            )}
          </div>
        )}

        {/* Payslips Tab */}
        {activeTab === 'payslips' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold text-foreground">Generated Payslips</h2>
              <div className="flex items-center gap-2">
                <select
                  className="px-3 py-2 border border-border rounded-md text-sm"
                  onChange={(e) => {
                    if (e?.target?.value) {
                      handleGeneratePayslips(e?.target?.value);
                    }
                  }}
                  disabled={payslipGenerating}
                >
                  <option value="">Generate for Payroll Run...</option>
                  {payrollRuns?.filter(r => r?.payrollStatus === 'processed')?.map((run) => (
                    <option key={run?.id} value={run?.id}>
                      {new Date(run?.payPeriodStart)?.toLocaleDateString()} - {new Date(run?.payPeriodEnd)?.toLocaleDateString()}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <PayslipList
              payslips={payslips}
              onGeneratePDF={handleDownloadPayslipPDF}
              loading={loading}
            />
          </div>
        )}

        {/* Processing Logs Tab */}
        {activeTab === 'logs' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold text-foreground">Bulk Processing Logs</h2>
              <Button variant="outline" iconName="RefreshCw" onClick={loadData}>
                Refresh
              </Button>
            </div>

            <BulkProcessingLog logs={processingLogs} loading={loading} />
          </div>
        )}
      </main>
      {/* Schedule Modal */}
      <ScheduleConfigModal
        isOpen={showScheduleModal}
        onClose={() => {
          setShowScheduleModal(false);
          setSelectedSchedule(null);
        }}
        onSave={handleSaveSchedule}
        existingSchedule={selectedSchedule}
      />

      <TeaserEditModal isOpen={showRedirectModal} onClose={closeModal} />
    </div>
  );
};

export default PayrollManagement;